import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Input,
  Stack,
  Text,
  Link,
  Heading,
  useToast,
  useColorModeValue,
  Container,
  VStack,
  Divider,
  Alert,
  AlertIcon,
  Select,
  Checkbox,
} from '@chakra-ui/react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import axios from 'axios';
import config from '../config';

const publicApi = axios.create({
  baseURL: config.API_BASE_URL,
  headers: {
    'Content-Type': 'application/json'
  },
  withCredentials: false // Explicitly disable credentials
});

const Register = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    phone: '',
    address: '',
    role: 'agent',
    parentAgentId: '',
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const { register } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [agentVerificationStatus, setAgentVerificationStatus] = useState({
    isVerified: false,
    message: '',
    agentName: ''
  });

  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');

  const verifyAgentId = async (agentId) => {
    if (!agentId || !agentId.match(/^AG\d{4}$/)) {
      setAgentVerificationStatus({
        isVerified: false,
        message: 'Invalid agent ID format',
        agentName: ''
      });
      return;
    }

    try {
      console.log('Verifying agent ID:', agentId);
      const response = await publicApi.get(`/api/users/by-agent-id/${agentId}`);
      console.log('Verification response:', response.data);

      if (response.data.success) {
        setAgentVerificationStatus({
          isVerified: true,
          message: 'Valid agent ID',
          agentName: response.data.data.name
        });
        setFormData(prev => ({
          ...prev,
          parentAgentId: agentId
        }));
      }
    } catch (error) {
      console.error('Agent verification error:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
        stack: error.stack
      });

      let errorMessage = 'Error verifying agent ID';
      if (error.response?.status === 404) {
        errorMessage = 'No approved agent found with this ID';
      } else if (error.response?.status === 500) {
        errorMessage = 'Server error. Please try again later.';
      }

      setAgentVerificationStatus({
        isVerified: false,
        message: errorMessage,
        agentName: ''
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!acceptedTerms) {
      setError('You must accept the Terms of Service to register');
      return;
    }
    setIsLoading(true);
    setError('');

    try {
      let submissionData = { ...formData };

      console.log('Attempting registration with:', {
        ...submissionData,
        password: '[REDACTED]'
      });
      
      await register(submissionData);
      
      toast({
        title: 'Registration successful',
        description: 'Please wait for admin approval',
        status: 'success',
        duration: 5000,
        isClosable: true,
      });
      navigate('/pending-approval');
    } catch (error) {
      console.error('Registration error:', {
        status: error.response?.status,
        data: error.response?.data,
        message: error.message
      });
      
      setError(
        error.response?.data?.message || 
        error.message || 
        'Failed to register'
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  return (
    <Box 
      minH="calc(100vh - 64px)"
      bg={useColorModeValue('gray.50', 'gray.900')}
      py={12}
    >
      <Container maxW="md" py={8}>
        <VStack
          spacing={8}
          bg={bgColor}
          borderRadius="xl"
          borderWidth="1px"
          borderColor={borderColor}
          p={8}
          shadow="xl"
        >
          <VStack spacing={3} w="full">
            <Heading 
              size="lg"
              bgGradient="linear(to-r, brand.500, brand.600)"
              bgClip="text"
            >
              Create Account
            </Heading>
            <Text color={useColorModeValue('gray.600', 'gray.400')}>
              Join our network of financial professionals
            </Text>
          </VStack>

          {error && (
            <Alert status="error" borderRadius="md">
              <AlertIcon />
              {error}
            </Alert>
          )}

          <form onSubmit={handleSubmit} style={{ width: '100%' }}>
            <VStack spacing={4} w="full">
              <FormControl isRequired>
                <FormLabel>Full Name</FormLabel>
                <Input
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="Enter your full name"
                  size="lg"
                />
              </FormControl>

              <FormControl isRequired>
                <FormLabel>Email</FormLabel>
                <Input
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="Enter your email"
                  size="lg"
                />
              </FormControl>

              <FormControl isRequired>
                <FormLabel>Password</FormLabel>
                <Input
                  name="password"
                  type="password"
                  value={formData.password}
                  onChange={handleChange}
                  placeholder="Create a password"
                  size="lg"
                />
              </FormControl>

              <FormControl isRequired>
                <FormLabel>Phone</FormLabel>
                <Input
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  placeholder="Enter your phone number"
                  size="lg"
                />
              </FormControl>

              <FormControl isRequired>
                <FormLabel>Address</FormLabel>
                <Input
                  name="address"
                  value={formData.address}
                  onChange={handleChange}
                  placeholder="Enter your address"
                  size="lg"
                />
              </FormControl>

              <FormControl isRequired>
                <FormLabel>Role</FormLabel>
                <Select
                  value={formData.role}
                  onChange={(e) => {
                    const newRole = e.target.value;
                    setFormData(prev => ({
                      ...prev,
                      role: newRole,
                      parentAgentId: newRole === 'agent' ? '' : prev.parentAgentId
                    }));
                  }}
                >
                  <option value="agent">Agent</option>
                  <option value="sub-agent">Sub Agent</option>
                </Select>
              </FormControl>

              {formData.role === 'sub-agent' && (
                <FormControl isRequired>
                  <FormLabel>Parent Agent ID</FormLabel>
                  <Input
                    placeholder="Enter Agent ID (e.g., AG0001)"
                    value={formData.parentAgentId}
                    onChange={(e) => {
                      const value = e.target.value.toUpperCase();
                      setFormData(prev => ({
                        ...prev,
                        parentAgentId: value
                      }));
                      
                      // Only verify if the format is correct
                      if (value.match(/^AG\d{4}$/)) {
                        verifyAgentId(value);
                      } else {
                        setAgentVerificationStatus({
                          isVerified: false,
                          message: value ? 'Enter ID in format AG0001' : '',
                          agentName: ''
                        });
                      }
                    }}
                    pattern="AG\d{4}"
                    title="Agent ID should be in format AG followed by 4 digits (e.g., AG0001)"
                  />
                  {formData.parentAgentId && (
                    <Text 
                      fontSize="sm" 
                      color={agentVerificationStatus.isVerified ? "green.500" : "red.500"} 
                      mt={1}
                    >
                      {agentVerificationStatus.isVerified ? (
                        <>
                          ✓ {agentVerificationStatus.message} - {agentVerificationStatus.agentName}
                        </>
                      ) : (
                        agentVerificationStatus.message || 'Enter a valid Agent ID in format AG0001'
                      )}
                    </Text>
                  )}
                </FormControl>
              )}

              <Box w="full">
                <Checkbox
                  isRequired
                  isChecked={acceptedTerms}
                  onChange={(e) => setAcceptedTerms(e.target.checked)}
                  colorScheme="brand"
                  size="md"
                >
                  <Text fontSize="sm">
                    I agree to the{' '}
                    <Link
                      to="/terms-of-service"
                      color="brand.500"
                      fontWeight="semibold"
                      textDecoration="underline"
                      isExternal
                      target="_blank"
                    >
                      Terms of Service
                    </Link>
                    {' '}and{' '}
                    <Link
                      to="/privacy-policy"
                      color="brand.500"
                      fontWeight="semibold"
                      textDecoration="underline"
                      isExternal
                      target="_blank"
                    >
                      Privacy Policy
                    </Link>
                  </Text>
                </Checkbox>
              </Box>

              <Button
                type="submit"
                colorScheme="brand"
                size="lg"
                width="full"
                isLoading={isLoading}
                loadingText="Creating account..."
                isDisabled={!acceptedTerms}
              >
                Register
              </Button>
            </VStack>
          </form>

          <VStack spacing={4} w="full" pt={4}>
            <Divider />
            <Text>
              Already have an account?{' '}
              <Link
                as={RouterLink}
                to="/login"
                color="brand.500"
                _hover={{ textDecoration: 'none', color: 'brand.600' }}
              >
                Sign in here
              </Link>
            </Text>
          </VStack>
        </VStack>
      </Container>
    </Box>
  );
};

export default Register;
